<template>
  <ak-card-header
    v-if="!assets.length || agencyOverview"
    class="flex flex--center"
  >
    <div class="flex__item flex flex--column">
      <h4 class="color-grey mb-0">
        Assets
        <span v-if="agencyOverview">in Production</span>
      </h4>
      <small v-if="!loading && assets.length"
        >Showing top {{ assets.length }}</small
      >
    </div>
    <ak-button
      v-if="!loading"
      color="primary-light"
      size="small"
      tag="router-link"
      :to="
        agencyOverview
          ? { name: 'assetsDirectory' }
          : { name: 'awaitingAssets' }
      "
    >
      View More
    </ak-button>
  </ak-card-header>
  <ak-card-body :size="'flush'" class="flex--column overflow-x-y h-full">
    <ak-display-table v-if="loading" cosy>
      <ak-display-table-row
        v-for="number in n"
        :key="number"
        class="c-skeleton-loading"
      >
        <ak-display-table-cell>
          <div class="c-faux-text width-150" />
        </ak-display-table-cell>
        <ak-display-table-cell>
          <div class="c-faux-text width-100" />
        </ak-display-table-cell>
        <ak-display-table-cell>
          <div class="c-faux-text width-75" />
        </ak-display-table-cell>
        <ak-display-table-cell>
          <div class="c-faux-text width-100" />
        </ak-display-table-cell>
        <ak-display-table-cell>
          <div class="c-faux-text width-75" />
        </ak-display-table-cell>
        <ak-display-table-cell class="cell--1">
          <div class="c-faux-text width-50" />
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>
    <ak-table
      v-if="!loading"
      v-model:data="assetsList"
      hoverable
      background
      spacing="medium"
      :empty-state="emptyState"
      :class="{ 'h-full flex': !assetsList.length }"
    >
      <template #default="{ row }">
        <ak-table-column label="Asset Name" :class="getBgColor(row)">
          <router-link
            :to="{
              name: 'singleCampaign',
              params: {
                quote: row.source.campaignQuote,
                aid: row.uid,
              },
            }"
          >
            <ak-pack :alignment="'top'">
              <ak-pack-item class="flex__item">
                <div class="text-bold color-black mb-tiny flex">
                  <ak-tooltip v-if="row.name.length > 30" placement="'bottom'">
                    <template #trigger>
                      {{ row.name.substring(0, 30) + '..' }}</template
                    >
                    {{ row.name }}
                  </ak-tooltip>
                  <div v-if="row.name.length <= 30">
                    {{ row.name }}
                  </div>
                </div>
                <ak-tooltip v-if="agencyOverview" :placement="'right'">
                  <template #trigger>
                    <ak-tag
                      v-if="agencyOverview"
                      color="secondary"
                      size="small"
                      nowrap
                    >
                      {{ date(row.deadlines.assets).format('ddd DD MMM YYYY') }}
                    </ak-tag>
                  </template>
                  Final Asset Deadline
                </ak-tooltip>
              </ak-pack-item>
            </ak-pack>
          </router-link>
        </ak-table-column>
        <ak-table-column
          label="Campaign Name"
          :class="getBgColor(row)"
          class="color-black"
        >
          <router-link
            :to="{
              name: 'singleCampaign',
              params: {
                quote: row.source.campaignQuote,
                aid: row.uid,
              },
            }"
          >
            {{ row.source.campaignName }}
          </router-link>
        </ak-table-column>
        <ak-table-column
          v-if="agencyOverview"
          label="Account Name"
          :class="getBgColor(row)"
          class="color-black"
        >
          <router-link
            :to="{
              name: 'singleCampaign',
              params: {
                quote: row.source.campaignQuote,
                aid: row.uid,
              },
            }"
          >
            <ak-tooltip
              v-if="row.account.name.length > 25"
              :placement="'bottom'"
            >
              <template #trigger>
                {{ row.account.name.substring(0, 25) + '..' }}</template
              >
              {{ row.account.name }}
            </ak-tooltip>
            <div v-if="row.account.name.length <= 25">
              {{ row.account.name }}
            </div>
          </router-link>
        </ak-table-column>
        <ak-table-column
          label=""
          :class="getBgColor(row)"
          class="text-right pr-none"
        >
          <router-link
            :to="{
              name: 'singleCampaign',
              params: {
                quote: row.source.campaignQuote,
                aid: row.uid,
              },
            }"
          >
            <ak-tooltip :placement="'right'">
              <template #trigger>
                <ak-avatar
                  v-if="row._additions.platformLogo"
                  class="width-30 h-30 circle u-image-fit min-width-30 b"
                  :src="row._additions.platformLogo"
                />
              </template>
              {{ row._additions.platformName }}
            </ak-tooltip>
          </router-link>
        </ak-table-column>
        <ak-table-column
          label="Ad Type"
          :class="getBgColor(row)"
          class="color-black"
        >
          <router-link
            :to="{
              name: 'singleCampaign',
              params: {
                quote: row.source.campaignQuote,
                aid: row.uid,
              },
            }"
          >
            {{ row._additions.adTypeName || '—' }}
          </router-link>
        </ak-table-column>
        <template
          v-for="(section, index) in row._additions.sections"
          :key="index"
        >
          <ak-table-column
            :label="getSectionLabel(index)"
            :class="getBgColor(row)"
            capitalize
            class="color-black"
          >
            <router-link
              :to="{
                name: 'singleCampaign',
                params: {
                  quote: row.source.campaignQuote,
                  aid: row.uid,
                },
              }"
            >
              <ak-tactic-part-status
                :tactic-section="section"
                :agency-user="agencyOverview"
                section="icon"
              />
            </router-link>
          </ak-table-column>
        </template>
        <ak-table-column
          v-if="
            agencyOverview &&
            row._additions.status.tag &&
            row._additions.status.tag.text
          "
          label="Final Deadline"
          :class="getBgColor(row)"
        >
          <router-link
            :to="{
              name: 'singleCampaign',
              params: {
                quote: row.source.campaignQuote,
                aid: row.uid,
              },
            }"
          >
            <ak-tag class="ml-tiny" :color="getTagColor(row)" nowrap>
              {{ row._additions.status.tag.text }}
            </ak-tag>
          </router-link>
        </ak-table-column>
      </template>
    </ak-table>
  </ak-card-body>
</template>

<script>
import { defineComponent, ref, watch } from 'vue'
import { currencyCodeToSymbol } from '@akerolabs/akero-vue/src/filters/currency'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import DisplayTable from '@akerolabs/akero-vue/src/components/displayTable/DisplayTable.vue'
import DisplayTableRow from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableRow.vue'
import DisplayTableCell from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableCell.vue'
import Table from '@akerolabs/akero-vue/src/components/table/Table.vue'
import TableColumn from '@akerolabs/akero-vue/src/components/table/TableColumn.vue'
import RouterLink from '../../../legacy/router/RouterLink.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Tooltip from '@akerolabs/akero-vue/src/components/tooltip/Tooltip.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'
import date from '@akerolabs/akero-vue/src/utils/date'
import TacticPartStatus from '../components/TacticPartStatus.vue'
import { execChangeHandler } from '../../../legacy/router/service'

export default defineComponent({
  name: 'AkWidgetAssetsProduction',

  components: {
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    [Icon.name]: Icon,
    [DisplayTable.name]: DisplayTable,
    [DisplayTableRow.name]: DisplayTableRow,
    [DisplayTableCell.name]: DisplayTableCell,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [RouterLink.name]: RouterLink,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Tooltip.name]: Tooltip,
    [Tag.name]: Tag,
    [TacticPartStatus.name]: TacticPartStatus,
  },

  props: {
    /**
     * @type {Array}
     * @description List of assets
     */
    assets: {
      type: Array,
      default: () => [],
    },
    /**
     * @type {Boolean}
     * @description Check loading state
     */
    loading: {
      type: Boolean,
      default: true,
    },
    /**
     * @type {String}
     * @description Selected User Id
     */
    selectedUser: {
      type: String,
      default: undefined,
    },
    /**
     * @type {String}
     * @description Selected Account Id
     */
    selectedAccount: {
      type: String,
      default: undefined,
    },
    /**
     * @type {Boolean}
     * @description Whether widget is in agency overview or not
     */
    agencyOverview: {
      type: Boolean,
    },
  },

  setup(props) {
    let n = 10
    const assetsList = ref(props.assets)

    /**
     * @param {String} str
     * @return {String}
     * @description Function to convert currency string to symbol
     */
    const getCurrencyCode = (str) => {
      return currencyCodeToSymbol(str)
    }

    /**
     * @param {Object}
     * @return {String}
     * @description Gets angular label class and converts to tag color
     */
    const getTagColor = (tag) => {
      if (
        tag._additions.status &&
        tag._additions.status.tag &&
        tag._additions.status.tag.colour
      ) {
        return tag._additions.status.tag.colour.split('--').pop()
      }
    }

    /**
     * @param {Object}
     * @return {String}
     * @description Gets background color for row if applicable
     */
    const getBgColor = (row) => {
      if (
        props.agencyOverview &&
        row._additions.status &&
        row._additions.status.background
      ) {
        return row._additions.status.background
      }
    }

    /**
     *
     * @description Create string for empty state depending on props
     */
    const emptyStateText = () => {
      let user = ref(props.selectedUser) ? ' for the selected user ' : ''
      let account = ref(props.selectedAccount) ? ' under this account ' : ''
      return 'There are currently no Assets to show ' + user + account
    }

    /**
     *
     * @description Create empty state for table
     */
    const emptyState = {
      title: props.agencyOverview ? 'Assets' : '',
      icon: props.agencyOverview ? 'images' : '',
      text: props.agencyOverview
        ? emptyStateText()
        : "Before your campaigns go live, we'll need to collect assets from you. Any awaiting assets will appear here. Get started now!",
      button: props.agencyOverview ? undefined : 'Create Brief',
      click: () =>
        execChangeHandler({
          name: 'advertising.manager.manage.brief_create',
        }),
    }

    const getSectionLabel = (index) => {
      switch (index) {
        case 0:
          return 'Details'
        case 1:
          return 'Copy'
        case 2:
          return 'Creative'
      }
    }

    watch(
      () => props.assets,
      (value) => {
        assetsList.value = value
      },
      { deep: true },
    )

    return {
      n,
      getCurrencyCode,
      getTagColor,
      getBgColor,
      assetsList,
      emptyState,
      date,
      getSectionLabel,
    }
  },
})
</script>
